//@flow
import FunctionStore from '../Base/functionStoreClass';
import { setFlag, apiUpdateState, updateState } from '../Base/storeUtils';
import Constants from '../../constants';
import CookiesData from '../../Utils/cookies-data';
import ApiInterface from '../../APIInterfaces/session';

import type { HandlerAdd, StoreState } from '../Base/functionStoreClass';

type SessionData = {
  access_token:string,
  user_id:string,
  expires_in:number,
  scope:string,
};

// this will update the token/userId pair and also reset all of the flags
function _setSessionData(state:StoreState, data:?SessionData) {
  state.setIn(['session', 'token'], data && data.access_token);
  state.setIn(['session', 'userId'], data && data.user_id);
  state.setIn(['session', 'scope'], data && data.scope);
  state.deleteIn(['flags']);
}

function _attemptLogin(store:SessionStore, data:{ username:string, password:string }) {
  CookiesData.set('email', { email: data.username });
  ApiInterface.getTokenWithUsernamePassword(data.username, data.password);
}

function _loginSuccessful(state:StoreState, data:SessionData) {
  CookiesData.setUser(data.access_token, data.user_id, data.expires_in, data.scope);
  _setSessionData(state, data);
}

function _createCustomerSuccessful(state:StoreState, data:{ token:SessionData }) {
  _loginSuccessful(state, data.token);
}

// on logout, we want to remove the cookie data and clear out all of the
// session data and flags.  any errors will be set after doing the reset.
function _logout(state:StoreState) {
  CookiesData.remove('auth');
  _setSessionData(state, null);
}

function _revokeToken(state:StoreState) {
  const token = _getToken(state);
  _logout(state);
  if (token) ApiInterface.revokeToken(token);
}

function _gotoHomepage() {
  window.location = '/products';
}

function _loginWithMagicLink(store:SessionStore, data:Object) {
  if (store.isLoggedIn()) return;
  ApiInterface.getTokenWithMagicLink(data.token);
}

function _loginWithSoftLink(store:SessionStore, data:Object) {
  if (store.isLoggedIn()) return;
  ApiInterface.getTokenWithSoftLink(data.token);
}

function _getToken(state:StoreState):?string {
  return state.getIn(['session', 'token'], null);
}

function _getScope(state:StoreState):?string {
  return state.getIn(['session', 'scope'], null);
}

export default class SessionStore extends FunctionStore {
  getToken():?string {
    return _getToken(this.getState());
  }

  getScope():?string {
    return _getScope(this.getState());
  }

  isLoginError():boolean {
    return this._getFlag('loginFailed');
  }

  isLoggedIn():boolean {
    // Old stored token cookies don't have the scope data
    return !!this.getToken() && (!this.getScope() || this.getScope() === 'customer');
  }

  isSoftAuth():boolean {
    return !!this.getToken() && this.getScope() === 'soft';
  }

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    onSuccess(Constants.LOGGED_IN, updateState(_setSessionData));
    onSuccess(Constants.LOGGED_OUT, _gotoHomepage);
    onSuccess(Constants.LOGIN_PENDING, _attemptLogin);
    onSuccess(Constants.LOGIN, apiUpdateState(_loginSuccessful));
    onSuccess(Constants.LOGIN_WITH_MAGIC_LINK, _loginWithMagicLink);
    onSuccess(Constants.LOGIN_WITH_SOFT_LINK, _loginWithSoftLink);

    onSuccess(Constants.CREATE_CUSTOMER_SUCCESS, apiUpdateState(_createCustomerSuccessful));

    // if there was an explicit logout request, we want to revoke the token
    onSuccess(Constants.LOGOUT, updateState(_revokeToken));
    onSuccess(Constants.REMOVE_LOGIN_ERROR, setFlag('loginFailed', false));
    onError(Constants.LOGIN, [ updateState(_logout), setFlag('loginFailed', true)]);

    // no matter what, clear out authentication info once it fails once
    onError(Constants.NOT_AUTHORIZED, updateState(_logout));
  }
}
